import { useMemo } from 'react';

import type { ColGroupDef } from 'ag-grid-enterprise';

import type { TwentileCount } from '@enterprise/api';

import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';
import TotalCellRenderer from '@components/grid/renderers/total-cell-renderer';

/**
 * Custom hook that returns column definitions for displaying twentile count data in an ag-Grid table.
 *
 * The columns are grouped into two main sections:
 * - "Mailing and List Information": Contains hidden columns for mailing ID and display name.
 * - "Score Information": Contains columns for twentile value
 * (with custom formatting for footer rows) and count (with aggregation).
 *
 * Column styling and header icons are applied for enhanced UI presentation.
 *
 * @returns {ColGroupDef<TwentileCount>[]} Array of column group definitions for ag-Grid.
 */
export const useGetTwentileCountColumns = () => {
  return useMemo(() => {
    return [
      {
        children: [
          {
            field: 'mailingId',
            headerName: 'Mailing ID',
            hide: true,
            width: 125,
          },
          {
            field: 'mailingDisplayName',
            headerName: 'Mailing',
            hide: true,
            width: 300,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: 'Mailing and List Information',
      },
      {
        children: [
          {
            cellClass: ['bg-teal-200', 'text-teal-900'],
            cellRenderer: TotalCellRenderer,
            field: 'twentile',
            headerClass: ['bg-teal-800'],
            headerName: 'Twentile',
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-teal-200', 'text-teal-900'],
            cellRenderer: NumberCellRenderer,
            field: 'quantityTwentileCount',
            headerClass: ['bg-teal-800'],
            headerName: 'Count',
            type: ['numericColumn'],
            width: 150,
          },
        ],
        headerClass: ['bg-teal-900'],
        headerGroupComponentParams: {
          icon: 'fa-users-medical',
        },
        headerName: 'Score Information',
      },
    ] as ColGroupDef<TwentileCount>[];
  }, []);
};
